/**
 * Shared defaults for every list page (Sampling, Subout, Analysis, Reporting,
 * Settings).  Centralised here so changes propagate without copy-paste drift.
 *
 * Mirrors the GoLive Settings ▸ Default Settings ▸ Date Filter page
 * (Settings → DateFilter_Category) row-for-row.  The default selection for
 * each FormCategory lines up with what GoLive's radio buttons ship with.
 */

// ── Page-size dropdown ───────────────────────────────────────────────────────

/**
 * Sentinel value used in PAGE_SIZES for the "All rows on one page" option.
 * Chosen so the existing `slice(start, start + pageSize)` math still works
 * (`Math.ceil(filtered.length / MAX_SAFE_INTEGER)` collapses to 1 page).
 */
export const ALL_PAGE_SIZE = Number.MAX_SAFE_INTEGER;

/** Standard page-size dropdown — 20 / 50 / 100 / 500 / All. */
export const PAGE_SIZES: readonly number[] = [20, 50, 100, 500, ALL_PAGE_SIZE];

/** Default rows-per-page on every list page — 100 (GoLive parity; user decision
 *  2026-07-02 keeping 100 over the prototype's 20 — logged in
 *  docs/ui-standardization-tracker.md conflict log). */
export const DEFAULT_PAGE_SIZE = 100;

/** Renders the sentinel as "All" in the dropdown; everything else as itself. */
export function pageSizeLabel(n: number): string {
  return n === ALL_PAGE_SIZE ? 'All' : String(n);
}

/**
 * Converts the UI page-size sentinel to a safe value for the API.
 * The backend `ModuleListArgs.PageSize` is a C# `int` (32-bit), so sending
 * `Number.MAX_SAFE_INTEGER` (9007199254740991) overflows to a negative value
 * and returns an empty result. Cap at the API's own clamp limit (10 000).
 */
export function wirePageSize(n: number): number {
  return n === ALL_PAGE_SIZE ? 10000 : n;
}

// ── Date-range dropdown ─────────────────────────────────────────────────────

export type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';

/** Options shown in every Date Range dropdown.  Order matches GoLive. */
export const DATE_OPTS: readonly DateRangeKey[] = ['1M', '3M', '6M', '1Y', '2Y', '5Y', 'All'];

/**
 * Maps a list page to one of GoLive's Date Filter categories.
 * One-to-one with the radio rows in Settings → Default Settings → Date Filter.
 *
 *   TaskWorkflow       → Task Calendar / Scheduled Tasks
 *   SampleTracking     → Sampling Allocation, Sample Registration, Receipt Notification
 *   SuboutTracking     → Subout Test Order, Subout Notification Queue
 *   AnalysisEntry      → Prep Queue, Prep Batches, Analysis Queue, QC Batches, Result Tracking
 *   AnalysisReview     → SDMS Validation, Result Approval
 *   ReportingWorkflow  → Custom Reporting, Report Validation, Delivery, Tracking, EDD, MOR, MSR
 *   FieldDataTracking  → Field Data Tracking
 *   SampleTransfer     → Sample Transfer
 *   InventoryWorkflow  → Inventory Workflow
 *   ReagentPreparation → Reagent Preparation
 */
export type FormCategory =
  | 'TaskWorkflow'
  | 'SampleTracking'
  | 'SuboutTracking'
  | 'AnalysisEntry'
  | 'AnalysisReview'
  | 'ReportingWorkflow'
  | 'FieldDataTracking'
  | 'SampleTransfer'
  | 'InventoryWorkflow'
  | 'ReagentPreparation';

/**
 * Default date-range per GoLive's Date Filter Category settings.  Most
 * categories ship with 3M; Task Workflow ships with All.
 *
 * Source: BTLIMS GoLive Settings → Default Settings → Date Filter (Category).
 */
export const DEFAULT_DATE_RANGE: Record<FormCategory, DateRangeKey> = {
  TaskWorkflow:       'All',
  SampleTracking:     '3M',
  SuboutTracking:     '3M',
  AnalysisEntry:      '3M',
  AnalysisReview:     '3M',
  ReportingWorkflow:  '3M',
  FieldDataTracking:  '3M',
  SampleTransfer:     '3M',
  InventoryWorkflow:  '3M',
  ReagentPreparation: '3M',
};

/**
 * Per-module default Date-Range, in days, for the generic ModuleListView grid
 * (Data Center / Audit Trail forms).  Mirrors each GoLive list-view's own
 * default "Date Filter" selection.  Modules not listed fall back to the
 * 3-month (90-day) default — GoLive ships most list views with "3M".
 *
 *   dc-collector (ClientCollectors) → GoLive ships with "3M" (90d).  Combined
 *   with the calendar-month cutoff in ModuleListReader.FromDateFor this yields
 *   the same 82-row window GoLive shows (LastUpdatedDate ≥ first-of-month-3-back).
 */
export const MODULE_DEFAULT_RANGE_DAYS: Record<string, number> = {
  'dc-collector': 90,
};

// ── Date-range cutoff helper ─────────────────────────────────────────────────

/**
 * Returns the cutoff Date for the given range key.  `'All'` returns null
 * (no filter applied).  Used by every list page's date-range filter.
 */
export function cutoffDate(key: DateRangeKey): Date | null {
  if (key === 'All') return null;
  const now = new Date();
  switch (key) {
    case '1M': return new Date(now.getFullYear(),     now.getMonth() - 1,  now.getDate());
    case '3M': return new Date(now.getFullYear(),     now.getMonth() - 3,  now.getDate());
    case '6M': return new Date(now.getFullYear(),     now.getMonth() - 6,  now.getDate());
    case '1Y': return new Date(now.getFullYear() - 1, now.getMonth(),      now.getDate());
    case '2Y': return new Date(now.getFullYear() - 2, now.getMonth(),      now.getDate());
    case '5Y': return new Date(now.getFullYear() - 5, now.getMonth(),      now.getDate());
  }
}
